import { DeleteOutlined, EditOutlined } from '@mui/icons-material'
import { Box } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { Context, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { impregnationEndPoints } from 'src/api/impregnation'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import InfiniteScrollTable from 'src/components/table/InfiniteScrollTable'
import { ImpregnationType, MainComponentStatus } from 'src/enums'
import { AuthorizationType } from 'src/enums/auth'
import { permissionValue } from 'src/enums/permissionValue'
import { IImpregnation } from 'src/generated-types/impregnation'
import { impregnationQueryKeys } from 'src/query-keys'
import { getFormattedDate } from 'src/utils'
import { getUserDetail, UserDetail } from 'src/utils/getUserDetails'
import NoImpregnation from '../../../../assets/svg/no-certificate.svg'

interface ImpregnationTableProps {
  context: Context<any> | null | undefined
  mainComponentStatus: MainComponentStatus
  mainComponentId?: EntityId
  editImpregnation?: (initialValues: IImpregnation) => void
  deleteImpregnation?: (id: EntityId) => void
  height?: string
  autoHeight?: boolean
  maxVisibleRowCount?: number
  minimalEmptyStatus?: boolean
  setDataLength?: (length: number) => void
  disabled?: boolean
}
const ImpregnationTable = ({
  context,
  mainComponentStatus,
  mainComponentId,
  editImpregnation,
  deleteImpregnation,
  height,
  autoHeight = false,
  maxVisibleRowCount,
  minimalEmptyStatus = false,
  setDataLength,
  disabled,
}: ImpregnationTableProps) => {
  const { t } = useTranslation(['impregnation', 'common'])

  const StaticColumns: GridColumns = useMemo(
    () => [
      {
        field: 'impregnationType',
        headerName: t('impregnation', { ns: 'impregnation' }),
        flex: 1,
        renderCell: params => (
          <DynamicTextTypography
            text={t(ImpregnationType[params.row.impregnationType].toString(), { ns: 'impregnation' }) || ''}
            fontWeight={500}
            maxLength={10}
          />
        ),
      },
      {
        field: 'impregnationDate',
        headerName: t('date', { ns: 'common' }),
        flex: 1,
        renderCell: params => getFormattedDate(params.row.impregnationDate),
      },
      {
        field: 'evaluation',
        headerName: t('evaluation', { ns: 'impregnation' }),
        flex: 1,
        renderCell: params =>
          params.row.evaluation != null && params.row.evaluation !== undefined && params.row.evaluation !== ''
            ? params.row.evaluation + '%'
            : '-',
      },
      { field: 'consumption', headerName: t('liters', { ns: 'impregnation' }), flex: 1 },
      {
        field: 'createdByUser',
        headerName: t('registered-by', { ns: 'impregnation' }),
        flex: 1,
        renderCell: params => (
          <DynamicTextTypography
            text={getUserDetail(params.row.createdByUser, UserDetail.FullName)}
            fontWeight={400}
            maxLength={10}
          />
        ),
      },
    ],
    [t],
  )

  const columns: GridColumns = useMemo(
    () => [
      ...StaticColumns,
      {
        field: 'actions',
        type: 'actions',
        width: mainComponentStatus == MainComponentStatus.InService ? 70 : 0,
        renderCell: params =>
          mainComponentStatus == MainComponentStatus.InService
            ? [
                <AuthorizationProvider
                  Context={context}
                  permissions={[permissionValue.ServiceStation.Update]}
                  type={AuthorizationType.Disabled}
                  key={'edit'}
                >
                  <GridActionButton
                    buttonIcon={<EditOutlined sx={{ width: '16px' }} />}
                    width="24px"
                    data-cy="UpdateImpregnation"
                    height="24px"
                    shadowWeight={0}
                    disabled={mainComponentStatus != MainComponentStatus.InService || disabled}
                    sx={{ marginRight: '8px' }}
                    onClick={() => editImpregnation && editImpregnation(params.row as IImpregnation)}
                  />
                </AuthorizationProvider>,
                <AuthorizationProvider
                  Context={context}
                  permissions={[permissionValue.ServiceStation.Delete]}
                  type={AuthorizationType.Disabled}
                  key={'delete'}
                >
                  <GridActionButton
                    buttonIcon={<DeleteOutlined sx={{ width: '16px' }} />}
                    width="24px"
                    data-cy="DeleteImpregnation"
                    height="24px"
                    shadowWeight={0}
                    disabled={mainComponentStatus != MainComponentStatus.InService || disabled}
                    onClick={() => deleteImpregnation && deleteImpregnation(params.row.id)}
                    aria-hidden={mainComponentStatus != MainComponentStatus.InService}
                  />
                </AuthorizationProvider>,
              ]
            : [],
      },
    ],
    [mainComponentStatus, disabled],
  )

  return (
    <InfiniteScrollTable<IImpregnation>
      id={'impregnation-table'}
      queryUrl={impregnationEndPoints.getImpregnations(mainComponentId as EntityId)}
      generateQueryKey={(page: number, pageSize: number) =>
        impregnationQueryKeys.GET_IMPREGNATIONS(mainComponentId as EntityId, { page, pageSize })
      }
      pageSize={10}
      columns={columns}
      height={height}
      autoHeight={autoHeight}
      maxVisibleRowCount={maxVisibleRowCount}
      getRowHeight={() => 36}
      minRowHeight="36px"
      containerProps={{
        sx: {
          '& .MuiDataGrid-virtualScroller': {
            paddingBottom: '0px !important',
            overflowX: 'hidden',
          },
          '& .MuiDataGrid-virtualScrollerContent': {
            paddingBottom: '0px !important',
          },
        },
      }}
      noDataComponent={() => (
        <Box height="100%" mt={2}>
          <EmptyStatusCard
            imageWidth={'85px'}
            image={NoImpregnation}
            header={t('impregnation-empty-status-title', { ns: 'impregnation' })}
            content={t('impregnation-empty-status-content', { ns: 'impregnation' })}
            imageContainerClasses="pb-4 pt-0"
            contentTopMargin="10px"
            headerFontSize="18px"
            contentFontSize="14px"
            contentClasses="mt-1 mb-1"
            height="100%"
            hideImage={minimalEmptyStatus}
            compact={minimalEmptyStatus}
          />
        </Box>
      )}
      hideDefaultFooterPagination
      spinnerHeight={100}
      setDataLength={setDataLength}
    />
  )
}

export default ImpregnationTable
